import type { ServiceStation } from './api.js';

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface ServiceStationWithDistance extends ServiceStation {
  distanceKm: number;
}

function searchText(value: string): string {
  return value.toLocaleLowerCase('ru-RU').replaceAll('ё', 'е').trim();
}

export function filterServiceStations(items: ServiceStation[], query: string): ServiceStation[] {
  const needle = searchText(query);
  if (needle.length < 2) return [];

  return items.filter((station) => searchText([
    station.organization,
    station.address,
    ...station.types,
  ].join(' ')).includes(needle));
}

/** Расстояние по прямой между двумя координатами по формуле гаверсинусов. */
export function distanceKm(from: Coordinates, to: Coordinates): number {
  const radians = (degrees: number) => degrees * Math.PI / 180;
  const earthRadiusKm = 6371;
  const latitudeDelta = radians(to.latitude - from.latitude);
  const longitudeDelta = radians(to.longitude - from.longitude);
  const fromLatitude = radians(from.latitude);
  const toLatitude = radians(to.latitude);

  const a = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function nearestServiceStations(
  items: ServiceStation[],
  location: Coordinates,
): ServiceStationWithDistance[] {
  return items
    .map((station) => ({
      ...station,
      distanceKm: distanceKm(location, station),
    }))
    .sort((left, right) => left.distanceKm - right.distanceKm
      || left.organization.localeCompare(right.organization, 'ru'));
}

export function formatDistance(distance: number): string {
  if (distance < 1) return `${Math.max(1, Math.round(distance * 1000))} м`;
  if (distance < 10) return `${distance.toFixed(1).replace('.', ',')} км`;
  return `${Math.round(distance)} км`;
}
